import React from 'react';
import { Button, Stack } from '@mui/material';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getUserBotsAPI,
  getUserRobotsForBotsAPI,
  updateBotAPI,
} from '../api/bot';
import { getRobots, getUserPId } from '../common/util';
import LabBadge from '../components/LabBadge';
import {
  MODAL_TYPE_BOT_INSTALL,
  MODAL_TYPE_BOT_UNINSTALL,
  MODAL_TYPE_BOT_HIDE_OFF,
  MODAL_TYPE_BOT_HIDE,
} from '../common/consts';
import TableAvatar from '../components/TableAvatar';

const MODAL_INITIAL = {
  open: false,
  loading: false,
  bots: [],
  type: null,
  robots: [],
  error: null,
  messages: [],
};
const PROCESS_INITIAL = {
  pending: true,
  result: null,
  message: null,
  error: null,
};
const initialState = {
  loading: false,
  list: [],
  contentItems: [],
  total: 0,
  handler: null,
  modal: MODAL_INITIAL,
  install: { ...PROCESS_INITIAL, pending: false },
  uninstall: { ...PROCESS_INITIAL, pending: false },
  update: { ...PROCESS_INITIAL, pending: false },
};
const MODULE_BOT = 'bot';

const createListData = ({ id, title, lab, prevent, category }) => {
  const botTitle = (
    <Stack direction="row" gap={1} alignItems="center">
      {lab && (
        <LabBadge
          {...{
            alt: `${title}의 아이콘`,
            id,
            variant: 'square',
          }}
        />
      )}
      {!lab && (
        <TableAvatar
          {...{
            type: 'icon',
            uuid: id,
            variant: 'square',
          }}
        />
      )}
      {title}
    </Stack>
  );

  const botInstall = (
    <Button
      name={MODAL_TYPE_BOT_INSTALL}
      color="secondary"
      variant="contained"
      disabled={prevent}
      size="small"
      sx={{ minWidth: 50 }}
    >
      설치
    </Button>
  );
  const botHide = (
    <Button
      name={MODAL_TYPE_BOT_HIDE}
      color="secondary"
      variant="contained"
      disabled={prevent}
      size="small"
      sx={{ minWidth: 50 }}
    >
      숨김
    </Button>
  );
  const botHideOff = (
    <Button
      name={MODAL_TYPE_BOT_HIDE_OFF}
      color="secondary"
      variant="contained"
      disabled={prevent}
      size="small"
      sx={{ minWidth: 50 }}
    >
      숨김 해제
    </Button>
  );
  const botUninstall = (
    <Button
      name={MODAL_TYPE_BOT_UNINSTALL}
      color="secondary"
      variant="contained"
      disabled={prevent}
      size="small"
      sx={{ minWidth: 50 }}
    >
      제거
    </Button>
  );

  const item = [
    { key: 'title', content: botTitle },
    { key: 'category', content: category },
    { key: 'install', content: botInstall },
    { key: 'hide', content: botHide },
    { key: 'hideoff', content: botHideOff },
    { key: 'uninstall', content: botUninstall },
  ];

  return { id, key: id, item, disabled: prevent };
};

export const getBotList = createAsyncThunk(
  `${MODULE_BOT}/getBotList`,
  async (conditions, { rejectWithValue }) => {
    try {
      if (!getRobots() || getRobots().length === 0) {
        return rejectWithValue('등록된 로봇이 없습니다.');
      }
      const {
        result,
        data: { total, list },
      } = await getUserBotsAPI({
        ...conditions,
        userId: getUserPId(),
      });
      if (result) {
        const contentItems = list.map((item) => createListData(item));
        return { total, list, contentItems };
      }
      return rejectWithValue('봇 목록 로드 중 에러가 발생했습니다.');
    } catch (error) {
      return rejectWithValue('봇 목록 로드 중 에러가 발생했습니다.');
    }
  },
);

export const setBot = createAsyncThunk(
  `${MODULE_BOT}/setBot`,
  async ({ robots, bots, use, install }, { rejectWithValue }) => {
    let state;
    if (use) {
      state = '숨김 해제';
    } else if (use === false) {
      state = '숨김';
    }
    if (install) {
      state = '설치';
    } else if (install === false) {
      state = '제거';
    }
    try {
      const { result, useResult, installResult } = await updateBotAPI({
        robots,
        bots,
        use,
        install,
      });
      if (result) {
        if (use || use === false) {
          return useResult;
        }
        if (install || install === false) {
          return installResult;
        }
      }
      return rejectWithValue(`봇 ${state} 도중 오류가 발생했습니다.`);
    } catch (error) {
      return rejectWithValue(`봇 ${state} 도중 오류가 발생했습니다.`);
    }
  },
);

export const openModal = createAsyncThunk(
  `${MODULE_BOT}/openModal`,
  async ({ userId, items, type }, { rejectWithValue }) => {
    try {
      const botList = items.reduce((prev, curr) => {
        const { id, prevent } = curr;
        if (type === 'botupdate' || !prevent) {
          prev.push(id);
        }
        return prev;
      }, []);
      const { result, bots, robots, messages } = await getUserRobotsForBotsAPI({
        userId,
        bots: JSON.stringify(botList),
        type,
      });
      if (result) {
        return { bots, robots, type, messages };
      }
      return rejectWithValue('봇 설치 정보 로드 중 오류가 발생했습니다.');
    } catch (error) {
      return rejectWithValue('봇 설치 정보 로드 중 오류가 발생했습니다.');
    }
  },
);

const botSlice = createSlice({
  name: MODULE_BOT,
  initialState,
  reducers: {
    closeModal: (state) => ({
      ...state,
      modal: { ...MODAL_INITIAL },
      install: { ...PROCESS_INITIAL, pending: false },
      uninstall: { ...PROCESS_INITIAL, pending: false },
      update: { ...PROCESS_INITIAL, pending: false },
    }),
    setClickHandler: (state, action) => ({
      ...state,
      handler: action.payload,
    }),
  },
  extraReducers: {
    [getBotList.pending.type]: (state) => ({ ...state, loading: true }),
    [getBotList.fulfilled.type]: (state, action) => ({
      ...state,
      loading: false,
      ...action.payload,
    }),
    [getBotList.rejected.type]: (state, action) => ({
      ...state,
      loading: false,
      listError: action.payload,
    }),
    [openModal.pending.type]: (state) => ({
      ...state,
      modal: {
        ...state.modal,
        loading: true,
        error: null,
        robots: [],
        bots: [],
      },
    }),
    [openModal.fulfilled.type]: (state, action) => ({
      ...state,
      modal: { ...state.modal, loading: false, open: true, ...action.payload },
    }),
    [openModal.rejected.type]: (state, action) => ({
      ...state,
      modal: { ...state.modal, loading: false, error: action.payload },
    }),
    [setBot.pending.type]: (state) => ({
      ...state,
      update: { ...PROCESS_INITIAL },
    }),
    [setBot.fulfilled.type]: (state, action) => ({
      ...state,
      update: { pending: false, result: true, message: action.payload },
    }),
    [setBot.rejected.type]: (state, action) => ({
      ...state,
      update: { pending: false, result: false, error: action.payload },
    }),
  },
});

const { reducer: BotReducer, actions } = botSlice;
export const { closeModal, setClickHandler } = actions;
export default BotReducer;
